import React, { useMemo, useState } from "react";
import type { VendorMasterRecord } from "./vendorMasterTypes";

interface Props {
  vendor: VendorMasterRecord;
  busy: boolean;
  onSubmit: (action: "deleteVendor" | "restoreVendor", reason: string, confirmationText: string) => Promise<void> | void;
}

export default function VendorLifecyclePanel({ vendor, busy, onSubmit }: Props) {
  const deleted = vendor.status === "DELETED";
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const linkedCount = useMemo(() => {
    const contracts = vendor.contractLinks as { electronicContractIds?: unknown[]; legacyContractIds?: unknown[] } | undefined;
    return (contracts?.electronicContractIds?.length || 0) + (contracts?.legacyContractIds?.length || 0) + Number(vendor.paymentRequestCount || 0) + Number(vendor.paymentMethodCount || 0);
  }, [vendor]);

  if (!open) return <button type="button" onClick={() => setOpen(true)} className={`min-h-[40px] rounded-xl px-3 text-xs font-black text-white ${deleted ? "bg-emerald-700" : "bg-red-600"}`}>{deleted ? "거래처 복원" : "거래처 삭제"}</button>;

  const confirmationRequired = !deleted;
  const ready = reason.trim() && (!confirmationRequired || confirmationText === "거래처 삭제");
  return (
    <div className={`rounded-2xl border p-3 ${deleted ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}>
      <p className="text-sm font-black text-slate-900">{deleted ? "삭제 거래처 복원" : "거래처 소프트 삭제"}</p>
      {!deleted && <p className="mt-1 text-xs font-bold leading-5 text-red-800">문서는 삭제하지 않습니다. 신규 계약·결제 선택에서 제외되며 기존 이력은 유지됩니다.</p>}
      {confirmationRequired && <p className="mt-1 text-xs font-black text-red-800">{linkedCount > 0 ? `확인된 연결 이력 ${linkedCount}건이 있습니다.` : "서버가 저장 직전 계약·결제 연결을 다시 확인합니다."} 아래에 “거래처 삭제”를 입력해 주세요.</p>}
      <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder={deleted ? "복원 사유" : "삭제 사유"} className="mt-3 min-h-[42px] w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold" />
      {confirmationRequired && <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} placeholder="거래처 삭제" className="mt-2 min-h-[42px] w-full rounded-xl border border-red-200 bg-white px-3 text-sm font-bold" />}
      <div className="mt-3 flex gap-2">
        <button type="button" disabled={busy || !ready} onClick={() => onSubmit(deleted ? "restoreVendor" : "deleteVendor", reason, confirmationText)} className={`min-h-[40px] rounded-lg px-4 text-sm font-black text-white disabled:bg-slate-300 ${deleted ? "bg-emerald-700" : "bg-red-600"}`}>{busy ? "처리 중..." : deleted ? "복원" : "삭제 처리"}</button>
        <button type="button" disabled={busy} onClick={() => { setOpen(false); setReason(""); setConfirmationText(""); }} className="min-h-[40px] rounded-lg border border-slate-200 bg-white px-4 text-sm font-black text-slate-700">취소</button>
      </div>
    </div>
  );
}
